import {
  addDoc,
  collection,
  doc,
  getDocs,
  onSnapshot,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { firestore } from "../firebase";

const attendanceCollection = collection(firestore, "attendance");
const usersCollection = collection(firestore, "users");

/**
 * Subscribes to the current user's attendance record for a specific date
 * (used to detect whether today's attendance has already been marked).
 * Mirrors the original inline listener in Attendance.js exactly, including
 * the absence of an error callback.
 */
export function subscribeTodayAttendance(userId, date, onData) {
  const q = query(
    attendanceCollection,
    where("userId", "==", userId),
    where("date", "==", date)
  );
  return onSnapshot(q, (snap) =>
    onData(snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() })
  );
}

/**
 * Subscribes to all attendance records for the current user
 * (used to render the monthly log). Mirrors the original inline listener
 * in Attendance.js exactly.
 */
export function subscribeMyAttendance(userId, onData) {
  const q = query(attendanceCollection, where("userId", "==", userId));
  return onSnapshot(q, (snap) =>
    onData(snap.docs.map((item) => ({ id: item.id, ...item.data() })))
  );
}

/**
 * Creates a new attendance document. Shared by:
 *  - Attendance.js markAttendance() (self-marked attendance)
 *  - AttendanceReport.js saveAttendanceCorrection() "no existing record" branch
 *    (admin-entered correction where no record existed yet)
 * Both call sites performed an identical addDoc(collection(firestore,"attendance"), data)
 * with different payloads; the payload construction stays in each page unchanged.
 */
export async function createAttendanceRecord(data) {
  return addDoc(attendanceCollection, data);
}

/**
 * Updates an existing attendance document. Used by AttendanceReport.js
 * saveAttendanceCorrection() "existing record" branch. Mirrors the original
 * updateDoc call exactly (caller still builds arrayUnion/serverTimestamp fields).
 */
export async function updateAttendanceRecord(recordId, data) {
  return updateDoc(doc(firestore, "attendance", recordId), data);
}

/**
 * Fetches all documents from the users collection. Used by AttendanceReport.js
 * to build the staff list for the admin/non-staff register view. Returns the
 * raw, unfiltered, unsorted array exactly as the original getDocs(...) call did;
 * the page keeps its own role filtering and name sorting unchanged.
 */
export async function fetchAttendanceUsers() {
  const snapshot = await getDocs(usersCollection);
  return snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
}

/**
 * Fetches attendance records for the report's date range. Mirrors the two
 * original query branches in AttendanceReport.js loadAttendance() exactly:
 *  - staff view: constrained to the current user's own records (userId ==)
 *  - admin/other views: constrained to the selected date range (date >= / <=)
 * Returns the raw array; the page keeps its own post-fetch date filter unchanged.
 */
export async function fetchAttendanceRecords({ isStaffView, userId, startDate, endDate }) {
  const attendanceQuery = isStaffView
    ? query(attendanceCollection, where("userId", "==", userId))
    : query(
        attendanceCollection,
        where("date", ">=", startDate),
        where("date", "<=", endDate)
      );
  const snapshot = await getDocs(attendanceQuery);
  return snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
}
